import React from "react";
import dayjs from "dayjs";
import { Alert, Container, Button } from "react-bootstrap";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import { validateWorkHistoryEntry } from "../../../utils/workHistoryEntry";
import {
  getWorkHistoryRequirementText,
  sortWorkHistoriesByDate,
  validateWorkHistoryCoverage,
} from "../../../utils/workHistoryCoverage";

const Step9ReviewWorkHistoryPage = ({
  form,
  setForm,
  dbWorkHistories = [],
  prevStep,
  setStep,
}) => {
  const localEntries = form.workHistories || [];
  const incompleteEntries = localEntries.filter((entry) => Object.keys(validateWorkHistoryEntry(entry)).length > 0);
  const sortedLocalEntries = sortWorkHistoriesByDate(
    localEntries.map((entry, index) => ({ ...entry, originalIndex: index })),
  );
  const combinedEntries = sortWorkHistoriesByDate([
    ...dbWorkHistories.map((entry) => ({ ...entry, historySource: "saved" })),
    ...sortedLocalEntries.map((entry) => ({ ...entry, historySource: "new" })),
  ]);
  const coverage = validateWorkHistoryCoverage([
    ...dbWorkHistories,
    ...localEntries,
  ]);

  const formatWorkMonth = (value, year) =>
    value ? dayjs(value).format("MM/YYYY") : year || "-";

  const getWorkHistoryTitle = (entry) =>
    entry.wh_type === "Unemployed"
      ? "Did not work"
      : entry.wh_name || entry.wh_type || "-";

  const getWorkHistoryDetails = (entry) =>
    [
      ["Type", entry.wh_type],
      [entry.wh_type === "School" ? "Education" : "Position", entry.wh_position],
      [
        "Location",
        [entry.wh_city, entry.wh_province, entry.wh_country]
          .filter(Boolean)
          .join(", "),
      ],
    ].filter(([, value]) => value);

  const handleDelete = (index) => {
    const updated = [...localEntries];
    updated.splice(index, 1);
    setForm((prev) => ({ ...prev, workHistories: updated }));
  };

  const handleEdit = (index) => {
    setForm((prev) => ({
      ...prev,
      editingWorkHistory: localEntries[index],
      editingIndex: index,
    }));
    setStep(8);
  };

  const handleNext = () => {
    if (incompleteEntries.length) return;
    if (!coverage.isValid) {
      alert(coverage.message);
      return;
    }

    setStep(10);
  };

  return (
    <Container className="my-5">
      <h4 className="mb-4">Review Work History</h4>
      <Alert variant={coverage.isValid ? "info" : "warning"}>
        <div>{getWorkHistoryRequirementText()}</div>
        {!coverage.isValid && <div className="mt-2">{coverage.message}</div>}
      </Alert>
      {incompleteEntries.length > 0 && (
        <Alert variant="warning">Complete the missing details in {incompleteEntries.length} history {incompleteEntries.length === 1 ? "entry" : "entries"} before continuing. Use the edit button beside each entry.</Alert>
      )}

      <h6>Your Work and School History</h6>
      {combinedEntries.length === 0 ? (
        <p>No history entries added yet.</p>
      ) : (
        <div className="employment-history-list">
          {combinedEntries.map((entry) => (
            <div
              className="employment-history-item"
              key={
                entry.historySource === "saved"
                  ? `saved-${entry.wh_id}`
                  : `new-${entry.originalIndex}`
              }
            >
              <div className="employment-history-content">
                <div className="employment-history-line">
                  <span className="employment-history-title">
                    {getWorkHistoryTitle(entry)}
                  </span>
                  <span>
                    <strong>Record:</strong>{" "}
                    {entry.historySource === "saved" ? "Previously saved" : "New"}
                  </span>
                  <span>
                    <strong>From:</strong> {formatWorkMonth(entry.wh_datefrom, entry.wh_datefrom_year)}
                  </span>
                  <span>
                    <strong>To:</strong>{" "}
                    {entry.wh_status === "Yes" ? "Present" : formatWorkMonth(entry.wh_dateto, entry.wh_dateto_year)}
                  </span>
                  {getWorkHistoryDetails(entry).map(([label, value]) => (
                    <span key={label}>
                      <strong>{label}:</strong> {value}
                    </span>
                  ))}
                </div>
                {entry.historySource === "new" && Object.keys(validateWorkHistoryEntry(entry)).length > 0 && (
                  <div className="text-danger small mt-2">Missing Information</div>
                )}
              </div>
              {entry.historySource === "new" && (
                <div className="employment-history-actions">
                  <button
                    type="button"
                    className="employment-history-edit"
                    onClick={() => handleEdit(entry.originalIndex)}
                    aria-label="Edit work history"
                  >
                    <FaPen />
                  </button>
                  <button
                    type="button"
                    className="employment-history-delete"
                    onClick={() => handleDelete(entry.originalIndex)}
                    aria-label="Delete work history"
                  >
                    <FaTrashAlt />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="work-history-review-nav">
        <Button
          className="work-history-add-more"
          variant="outline-primary"
          onClick={() => setStep(8)}
        >
          + Add more
        </Button>
        <Button variant="secondary" onClick={prevStep}>
          ← Back
        </Button>
        <Button variant="primary" onClick={handleNext} disabled={incompleteEntries.length > 0 || !coverage.isValid}>
          Next →
        </Button>
      </div>
    </Container>
  );
};

export default Step9ReviewWorkHistoryPage;
